import { SimpleFaker } from '@faker-js/faker';
import { faker } from '@faker-js/faker/locale/en';
import { makeData } from 'mocks/helpers';
import type {
  AvailableCertDto,
  ComplianceDocumentDto,
  ComplianceDocumentListItemDto,
  ComplianceWarningDetail,
  DocumentStatus,
} from 'features/compliance/types';

/**
 * Deterministic MirageJS fixtures for the Compliance Document Generator (CDG).
 * Seeded with faker so the data is stable across runs (safe for snapshot tests).
 * Imported by `mocks/api/handlers/compliance.ts`.
 */

/** Fixed reference date so faker-generated timestamps stay deterministic. */
const REF_DATE = '2026-06-01T00:00:00.000Z';

/** The one document fixture with a NULL `complianceId` and zero cert files (DL-61574) —
 * a zero-cert generate; served by the list and by GET /api/compliance-document/:id. */
export const CERTLESS_DOCUMENT_ID = 8006;

// Dedicated instance so seeding for GUIDs never perturbs the module's global `faker` sequence.
const guidFaker = new SimpleFaker();

/** Deterministic GUID for a given seed. */
const guid = (seed: number): string => {
  guidFaker.seed(seed);
  return guidFaker.string.uuid();
};

const STATUSES: DocumentStatus[] = ['Generated', 'Draft', 'ExpiringSoon', 'Expired', 'Archived'];

/** Sample skip-and-warn warnings — reused by the partial-success job branch and doc-DTO fixtures. */
export const sampleDownloadWarnings: ComplianceWarningDetail[] = [
  { type: 'merge_failed_missing_blob', field: 'certificateFileIds', value: guid(3005) },
  { type: 'merge_failed_password_protected_pdf', field: 'certificateFileIds', value: guid(3006) },
];

/** Stable creator GUIDs (5 distinct users) + display names for the list enrichment. */
const CREATOR_GUIDS = makeData(5, j => guid(9100 + j));
const CREATOR_NAMES = ['Anna Holm', 'Jonas Berg', 'Mette Larsen', 'Elif Demir', 'Sara Lind'];

const TEMPLATE_NAMES: Record<number, string> = {
  1: 'EU Declaration of Conformity',
  2: 'REACH Compliance Statement',
  3: 'CPSIA Certificate',
  4: 'California Proposition 65 Statement',
  5: 'OEKO-TEX Standard 100 Statement',
};

/** Realistic fashion-PLM style names — one per document row (cycled). */
const STYLE_NAMES = [
  'Aurora Linen Shirt',
  'Nova Denim Jacket',
  'Meadow Floral Midi Dress',
  'Atlas Merino Crewneck',
  'Harbor Twill Chino',
  'Solstice Silk Blouse',
  'Ridge Quilted Vest',
  'Coastal Poplin Shirt',
  'Ember Corduroy Overshirt',
  'Willow Rib-Knit Cardigan',
  'Drift Cargo Short',
  'Lumen Satin Slip Dress',
  'Summit Down Parka',
  'Vale Pleated Skirt',
  'Onyx Leather Belt',
  'Fern Organic Tee',
  'Cove Seersucker Blazer',
  'Isla Crochet Top',
  'Slate Wool Trouser',
  'Prism Puffer Jacket',
  'Sage Jersey Polo',
  'Tide Board Short',
  'Astra Sequin Camisole',
  'Birch Flannel Shirt',
  'Terra Suede Skirt',
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `dd-MMM-yyyy` (e.g. `04-May-2026`) — mirrors the BE's saved-FileName date stamp. */
const fileNameStamp = (iso: string): string => {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${dd}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;
};

// ---------------------------------------------------------------------------
// Documents — 25 rows spanning every DocumentStatus value
// ---------------------------------------------------------------------------

const newComplianceDocument = (i: number): ComplianceDocumentDto => {
  faker.seed(1000 + i);
  const templateId = 1 + (i % 5);
  const styleId = 5000 + i;
  // One row is a ZERO-CERT document (DL-61574): generated from a style with no available
  // certificates — null complianceId anchor and no merged cert files (certCount 0).
  const certless = 8000 + i === CERTLESS_DOCUMENT_ID;
  const certFileCount = certless ? 0 : 1 + (i % 3);
  const createdAt = faker.date.recent({ days: 90, refDate: REF_DATE }).toISOString();
  return {
    // Live ids are numeric (BIGINT identity) — stable 8000+ sequence.
    id: 8000 + i,
    organizationId: 1,
    templateId,
    templateEditCount: faker.number.int({ min: 0, max: 12 }),
    styleId,
    complianceId: certless ? null : 9000 + (i % 7),
    // Mirrors the BE's BuildFileName: `{StyleName} - {TemplateName} - {dd-MMM-yyyy}.pdf`.
    fileName: `${STYLE_NAMES[i % STYLE_NAMES.length]} - ${
      TEMPLATE_NAMES[templateId]
    } - ${fileNameStamp(createdAt)}.pdf`,
    status: STATUSES[i % STATUSES.length],
    dataSnapshotHash: faker.string.hexadecimal({ length: 64, prefix: '', casing: 'lower' }),
    notes: i % 4 === 0 ? faker.lorem.sentence() : null,
    certFiles: makeData(certFileCount, j => ({
      complianceCertificateFileId: guid(2000 + i * 10 + j),
      // Merge order is 1-based on live.
      position: j + 1,
      fileHash:
        j % 2 === 0 ? faker.string.hexadecimal({ length: 64, prefix: '', casing: 'lower' }) : null,
    })),
    // Signature aggregate (DL-61574 B2): null when no appendices (certless row);
    // otherwise a deterministic mix of all-signed (even i) / not-all-signed (odd i).
    allAppendicesSigned: certless ? null : i % 2 === 0,
    createdBy: CREATOR_GUIDS[i % 5],
    createdAt,
    lastUpdated: faker.date.recent({ days: 10, refDate: REF_DATE }).toISOString(),
    // Persisted skip-and-warn rows — one doc in eight carries them, rest null.
    warnings: i % 8 === 0 ? sampleDownloadWarnings : null,
  };
};

export const complianceDocuments: ComplianceDocumentDto[] = makeData(25, newComplianceDocument);

export const complianceDocumentListItems: ComplianceDocumentListItemDto[] = complianceDocuments.map(
  (d, i) => ({
    id: d.id,
    fileName: d.fileName,
    status: d.status,
    templateId: d.templateId,
    templateName: TEMPLATE_NAMES[d.templateId],
    styleId: d.styleId,
    styleName: STYLE_NAMES[i % STYLE_NAMES.length],
    styleUserDefinedId: `ST-${1000 + i}`,
    complianceId: d.complianceId,
    certCount: d.certFiles.length,
    allAppendicesSigned: d.allAppendicesSigned,
    createdBy: d.createdBy,
    createdByName: CREATOR_NAMES[i % 5],
    createdAt: d.createdAt,
    lastUpdated: d.lastUpdated,
  })
);

// ---------------------------------------------------------------------------
// Available certs — covers Style-only / Style+Item / expired / inaccessible /
// pending / multi-file (1:N) variants (AC of DL-59571)
// ---------------------------------------------------------------------------

export const availableCerts: AvailableCertDto[] = [
  // Style source, accessible, single file
  {
    complianceId: 9001,
    source: 'Style',
    sourceId: 5001,
    sourceName: 'Summer Tee',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: false,
    disabledReason: null,
    files: [{ certificateFileId: guid(3001), fileName: 'reach-declaration.pdf', position: 1 }],
    certNumber: 'RC-1001',
    certificateTypeName: 'REACH Declaration',
    institution: 'SGS SA',
    issueDate: '2025-01-16',
    expiryDate: '2027-01-15',
    certState: 'Valid',
  },
  // Item source, accessible, MULTI-file (1:N) — selecting the card takes all its files.
  {
    complianceId: 9002,
    source: 'Item',
    sourceId: 7002,
    sourceName: 'Organic Cotton 180gsm',
    sourceCategory: 'Fabric, Yarn',
    isAccessible: true,
    isDisabled: false,
    disabledReason: null,
    files: [
      { certificateFileId: guid(3002), fileName: 'oekotex-page1.pdf', position: 1 },
      { certificateFileId: guid(3003), fileName: 'oekotex-page2.pdf', position: 2 },
      { certificateFileId: guid(3004), fileName: 'oekotex-annex.pdf', position: 3 },
    ],
    certNumber: 'OT-2002',
    certificateTypeName: 'OEKO-TEX Standard 100',
    institution: 'Hohenstein Institute',
    issueDate: '2025-09-01',
    expiryDate: '2026-09-30',
    certState: 'Valid',
  },
  // Expired → disabled
  {
    complianceId: 9003,
    source: 'Style',
    sourceId: 5003,
    sourceName: 'Winter Coat',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Expired',
    files: [{ certificateFileId: guid(3005), fileName: 'cpsia-expired.pdf', position: 1 }],
    certNumber: 'CP-3003',
    certificateTypeName: 'CPSIA Certificate',
    institution: 'Bureau Veritas',
    issueDate: '2022-02-02',
    expiryDate: '2024-02-01',
    certState: 'Expired',
  },
  // Inaccessible → disabled (still "Valid" by date; the file just can't be included)
  {
    complianceId: 9004,
    source: 'Item',
    sourceId: 7004,
    sourceName: 'Recycled Polyester',
    sourceCategory: 'Fabric',
    isAccessible: false,
    isDisabled: true,
    disabledReason: 'Inaccessible',
    files: [{ certificateFileId: guid(3006), fileName: 'grs-certificate.pdf', position: 1 }],
    certNumber: 'GR-4004',
    certificateTypeName: 'Global Recycled Standard',
    institution: 'Control Union',
    issueDate: '2025-06-02',
    expiryDate: '2027-06-01',
    certState: 'Valid',
  },
  // Pending approval → disabled
  {
    complianceId: 9005,
    source: 'Style',
    sourceId: 5005,
    sourceName: 'Denim Jacket',
    sourceCategory: null,
    isAccessible: true,
    isDisabled: true,
    disabledReason: 'Pending approval',
    files: [{ certificateFileId: guid(3007), fileName: 'reach-pending.pdf', position: 1 }],
    certNumber: 'PD-5005',
    certificateTypeName: 'REACH Declaration',
    institution: 'Intertek',
    issueDate: '2026-01-02',
    expiryDate: '2028-01-01',
    certState: 'Draft',
  },
];

// ---------------------------------------------------------------------------
// Reused lookups
// ---------------------------------------------------------------------------

// The generator lookups (templates / template fields / styles / available certs served
// by routes) live in `mocks/data/complianceDocGenerator.ts` — the live-shape fixture set.

// ---------------------------------------------------------------------------
// Error triggers
// ---------------------------------------------------------------------------

/** Document id that DELETE treats as forbidden (→ 403 `action_not_allowed`). */
export const FORBIDDEN_DELETE_DOC_ID = 8255;

/** List row whose stored blob the download route treats as missing (→ 404
 * `entity_not_found`) — a document deleted server-side after the list snapshot. */
export const DOCUMENT_BLOB_MISSING_ID = 8002;
